import React, { useEffect, useRef } from "react";
import { ItemList, ItemListColumn, ItemListRow } from "@/components/itemList";
import LoadingPanel from "@/components/loadingPanel";
import { useConversations } from "@/hooks/ai-agent/useConversations";
import { useDialogModal } from "@/hooks/useDialogModal";
import { formatDateRelative } from "@/i18n";

const ConversationMenu = ({ companyId, onSelect }) => {
  const { data, isLoading, isError } = useConversations(companyId);
  const conversations = data?.conversations ?? [];

  if (isLoading) {
    return <LoadingPanel />;
  }

  if (isError) {
    return (
      <div className="text-destructive">Could not load conversations.</div>
    );
  }

  if (conversations.length === 0) {
    return <div className="text-muted-foreground">No conversations yet.</div>;
  }

  return (
    <ItemList columns={[{ id: "title" }]} hideColumns virtualized={false}>
      {conversations.map((conversation) => (
        <ItemListRow
          key={conversation.id}
          onClick={() => onSelect(conversation.id)}
        >
          <ItemListColumn>
            <div className="flex flex-col">
              <span className="truncate font-semibold">
                {conversation.title}
              </span>
              <span>{formatDateRelative(conversation.lastActivityAt)}</span>
            </div>
          </ItemListColumn>
        </ItemListRow>
      ))}
    </ItemList>
  );
};

const ConversationSelector = ({ companyId, onClose, onSelect }) => {
  const { prompt, dialogElement } = useDialogModal();
  const hasOpened = useRef(false);

  useEffect(() => {
    if (hasOpened.current) {
      return;
    }

    hasOpened.current = true;

    void prompt(
      "Conversations",
      ({ onSubmit }) => (
        <ConversationMenu companyId={companyId} onSelect={onSubmit} />
      ),
      { submitText: null, size: "sm" },
    ).then((conversationId) => {
      if (conversationId) {
        onSelect(conversationId);
      } else {
        onClose();
      }
    });
  }, [companyId, onClose, onSelect, prompt]);

  return dialogElement;
};

export { ConversationSelector };
